import React from 'react';
import { ScanBarcode, Volume2, VolumeX, Zap, Wand2 } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { getTheme } from './themeUtils';
import { ScanMode } from '../types';

interface ScanInputProps {
  barcodeInput: string;
  scanMode: ScanMode;
  isRapidMode: boolean;
  isMuted: boolean;
  mainInputRef: React.RefObject<HTMLInputElement>;
  onInputChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onScan: () => void;
  onToggleRapidMode: () => void;
  onToggleMute: () => void;
  onGenerateInternalEan: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const ScanInput: React.FC<ScanInputProps> = ({
  barcodeInput,
  scanMode,
  isRapidMode,
  isMuted,
  mainInputRef,
  onInputChange,
  onKeyDown,
  onScan,
  onToggleRapidMode,
  onToggleMute,
  onGenerateInternalEan,
  onBlur,
}) => {
  const theme = getTheme(scanMode);

  return (
    <div
      className={`bg-card rounded-xl shadow-lg p-4 md:p-6 border-2 border-gray-200 dark:border-gray-700 relative overflow-hidden ${
        isRapidMode ? 'border-amber-500 dark:border-amber-400 border-4' : theme.border
      }`}
    >
      {/* Top Badges */}
      <div
        className={`absolute top-0 right-0 text-white px-3 py-1 rounded-bl-lg text-xs font-bold tracking-wider ${theme.bg}`}
      >
        AKTÍV
      </div>

      {isRapidMode && (
        <div className="absolute top-0 left-0 bg-amber-500 text-white px-3 py-1 rounded-br-lg text-xs font-bold tracking-wider flex items-center gap-1">
          <Zap size={12} />
          RAPID MODE
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-4 pr-12">
        <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ScanBarcode className={theme.text} size={24} />
          <span className="hidden sm:inline">Vonalkód Beolvasása</span>
          <span className="sm:hidden">Szkennelés</span>
        </h2>

        <div className="flex gap-2 shrink-0">
          <Tooltip content="Rapid módban a beolvasott tételek azonnal, megerősítés nélkül mentésre kerülnek.">
            <button
              onClick={onToggleRapidMode}
              className={`h-10 w-10 rounded-lg transition-all flex items-center justify-center ${
                isRapidMode
                  ? 'bg-amber-500 text-white shadow-md hover:bg-amber-600'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              aria-label={isRapidMode ? 'Rapid mód kikapcsolása' : 'Rapid mód bekapcsolása'}
            >
              <Zap size={20} />
            </button>
          </Tooltip>

          <Tooltip content="Hangjelzések ki/be kapcsolása.">
            <button
              onClick={onToggleMute}
              className={`h-10 w-10 rounded-lg transition-all flex items-center justify-center ${
                isMuted
                  ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  : 'bg-primary text-white shadow-md hover:bg-primary/90'
              }`}
              aria-label={isMuted ? 'Hang bekapcsolása' : 'Hang kikapcsolása'}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="flex w-full gap-2">
        <input
          ref={mainInputRef}
          type="text"
          className={`flex-1 min-w-0 text-lg sm:text-xl md:text-2xl font-mono py-3 md:py-4 px-3 md:px-4 border-2 rounded-xl outline-none transition tracking-widest uppercase bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 ${
            isRapidMode
              ? 'border-amber-500 dark:border-amber-400 focus:border-amber-600 dark:focus:border-amber-500'
              : theme.border
          }`}
          placeholder={isRapidMode ? 'Gyors beolvasás' : 'Beolvasás egyesével'}
          value={barcodeInput}
          onChange={(e) => {
            const sanitized = e.target.value.replace(/ö/g, '0').replace(/Ö/g, '0').trim();
            onInputChange(sanitized);
          }}
          onKeyDown={onKeyDown}
          autoFocus
          onBlur={onBlur}
        />
        <button
          onClick={onScan}
          disabled={isRapidMode}
          className={`text-white px-4 md:px-6 py-3 md:py-4 rounded-xl font-bold transition shadow-md disabled:opacity-50 shrink-0 text-sm md:text-base ${theme.bg} ${theme.hover}`}
          title={isRapidMode ? 'Rapid módban az Enter gomb automatikusan elküldi' : 'Küldés'}
        >
          OK
        </button>
      </div>

      {/* Generate Internal Barcode Button */}
      <button
        onClick={onGenerateInternalEan}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 border-2 border-slate-300 dark:border-gray-600 text-slate-500 dark:text-gray-400 hover:text-accent hover:border-accent rounded-lg transition font-medium text-sm bg-white dark:bg-gray-700"
        type="button"
      >
        <Wand2 size={16} />
        Nincs vonalkód? Generálás (20...)
      </button>
    </div>
  );
};
